import React, { useCallback } from 'react';
import { Panel } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { IconProvider } from '../../../ui';
import { toast } from 'sonner';

export interface HelperLinesControlsProps {
  /** Position of the panel (only used if withPanel is true) */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  /** Whether to wrap in a Panel component (default: true) */
  withPanel?: boolean;
  /** Current state of helper lines */
  enabled?: boolean;
  /** Callback when toggle state changes */
  onToggle?: (enabled: boolean) => void;
}

/**
 * Helper Lines controls for React Flow
 * Provides a button to toggle alignment helper lines for node positioning
 */
export function HelperLinesControls({ 
  position = 'top-right', 
  withPanel = true,
  enabled = false,
  onToggle
}: HelperLinesControlsProps) {
  const handleToggle = useCallback(() => {
    const newState = !enabled;
    onToggle?.(newState);
    
    toast.success(newState ? 'Helper lines enabled' : 'Helper lines disabled', {
      description: newState 
        ? 'Drag nodes to see alignment guides' 
        : 'Helper lines are now hidden',
    });
  }, [enabled, onToggle]);

  const content = (
    <Button
      variant={enabled ? "default" : "ghost"}
      size="iconButton"
      onClick={handleToggle}
      title={enabled ? "Disable helper lines" : "Enable helper lines"}
    >
      <IconProvider 
        iconstring="fa-ruler-combined" 
        style="light" 
        size={16}
      />
    </Button>
  );

  return withPanel ? (
    <Panel position={position} className="m-2">
      {content}
    </Panel>
  ) : content;
}
